<script setup lang="ts">
import type { ReportColumn } from '../../models/column.types'
import type { DataVisibleCardMediaType } from '../../models/dataVisible.types'
import type { ReportFilter } from '../../models/filter.types'
import type { ReportRow } from '../../models/report.types'

import ReportMediaItem from './ReportMediaItem.vue'

import { Button, Skeleton, Spinner } from '@/shared/ui'
import type { UiAsyncState } from '@/shared/ui/types'

// The card VIEW — v3 `OrphexReportMediaCard.vue` (507 ln) with its entire fetch/filter/settings
// block deleted rather than ported (ORP-18651 AC1). v3 held a private third copy of the request
// pipeline: `useReportRequest` + an AbortController, `OrphexFilters` + a `customizeStore` filter
// cache, `OrphexTableSettings` + a column cache, a sort-metric select and an infinite-scroll pager
// (`:154-167`, `:276-437`). In v4 all of that already runs ONCE in `useDataVisible`
// (`useReportData` + `useReportFilters` + `useReportTableViewState` + `ReportPagination`), so this
// component is a pure presenter over props — the same deal `ReportTablePresenter` and `ReportChart`
// take. It deliberately imports no api/, store/ or composable: adding one would re-create the fork.
const props = withDefaults(
  defineProps<{
    rows: readonly ReportRow[]
    dimensions: readonly ReportColumn[]
    metrics: readonly ReportColumn[]
    state: UiAsyncState
    /** Marketing channel this surface is pinned to — a logo-less metric borrows it. */
    channel?: string
    mediaType?: DataVisibleCardMediaType
    /** Forces every tile to the ad-library variant; individual rows can still opt in (see below). */
    variant?: 'default' | 'adLibrary'
    ariaLabel: string
    emptyLabel: string
    errorLabel: string
    retryLabel: string
    unavailableLabel: string
    filterable?: boolean
    refreshing?: boolean
  }>(),
  { mediaType: 'Image', variant: 'default', channel: '', filterable: false, refreshing: false },
)

const emit = defineEmits<{ reload: []; 'add-filter': [filter: ReportFilter] }>()

/**
 * v3 `OrphexReportMediaCard.vue:58-59` — `type === 'Ad Library' || datum?.preview_ss_url`. The
 * page-level half is the `variant` prop; the per-row half stays row-driven because a mixed response
 * (ad-library creatives inside an ordinary creative report) renders both tiles side by side.
 */
function variantFor(row: ReportRow): 'default' | 'adLibrary' {
  return props.variant === 'adLibrary' || row['preview_ss_url'] ? 'adLibrary' : 'default'
}
</script>

<template>
  <ul
    v-if="state === 'loading'"
    class="report-media-card__grid grid gap-md py-2xs m-0 list-none"
    aria-busy="true"
    data-testid="report-media-loading"
    :aria-label="ariaLabel"
  >
    <li v-for="index in 6" :key="index" class="report-media-card__item min-w-0">
      <div
        class="report-media-card__loading-tile flex min-h-88 flex-col p-xs border border-border rounded-md gap-md bg-surface"
      >
        <Skeleton height="12.75rem" radius="var(--ui-radius-sm)" />
        <Skeleton height="1.25rem" width="45%" />
        <Skeleton height="0.75rem" :lines="3" />
      </div>
    </li>
  </ul>
  <div
    v-else-if="state === 'empty'"
    class="report-media-card__state flex min-h-32 items-center justify-center gap-sm text-muted"
    data-testid="report-media-empty"
  >
    {{ emptyLabel }}
  </div>
  <div
    v-else-if="state === 'error'"
    class="report-media-card__state flex min-h-32 items-center justify-center gap-sm text-muted"
    data-testid="report-media-error"
  >
    <span>{{ errorLabel }}</span>
    <Button size="sm" variant="outline" @click="emit('reload')">{{ retryLabel }}</Button>
  </div>
  <div v-else class="relative">
    <ul
      class="report-media-card__grid grid gap-md py-2xs m-0 list-none"
      :aria-label="ariaLabel"
      :aria-busy="refreshing || undefined"
    >
      <li v-for="(row, index) in rows" :key="index" class="report-media-card__item min-w-0">
        <ReportMediaItem
          :row="row"
          :dimensions="dimensions"
          :metrics="metrics"
          :channel="channel"
          :media-type="mediaType"
          :variant="variantFor(row)"
          :alt="ariaLabel"
          :unavailable-label="unavailableLabel"
          :filterable="filterable"
          @add-filter="emit('add-filter', $event)"
        />
      </li>
    </ul>
    <div
      v-if="refreshing"
      class="absolute inset-0 z-40 rounded-md bg-surface/60"
      data-testid="report-media-refreshing"
    >
      <div class="sticky top-1/3 flex justify-center py-lg">
        <Spinner size="lg" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.report-media-card__grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

@media (max-width: 63.9375rem) {
  .report-media-card__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 47.9375rem) {
  .report-media-card__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
